import { Divider, Flex, HStack, Text, VStack } from "@chakra-ui/react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import AppText, { FormLabel } from "../../components/Core/AppText/AppText";
import GuaranteedHoursProgress from "../../components/GuaranteedHours/GuaranteedHoursProgress";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import MonthSelector from "../../components/Shared/MonthSelector";
import { Schedule, ScheduleRow } from "../../components/Shared/Schedule/Schedule";
import { APPLICATION_STATUS } from "../../configs/constants/ApplicationStatus";
import { TooltipType } from "../../configs/schedule.config";
import Consultant from "../../models/Consultant";
import Job, { ApplicationWithExpansion, Shift } from "../../models/Job";
import { SalaryStatItem } from "../../models/Salary";
import {
	useConsultantApplications,
	useConsultantSalarySummary,
} from "../../networking/consultant.hooks";
import { useOrganisation } from "../../networking/organisation.hooks";
import { getServiceName } from "../../utils/competence.utils";
import { formatCurrencySEK, formatNumber } from "../../utils/date.time.utils";
import { getNumberValue } from "../../utils/object.utils";
import { customerRoute, jobRoute } from "../../utils/route.utils";

dayjs.extend(utc);

type ApplicationWithJobAndShifts = ApplicationWithExpansion<{ job: Job }>;

const ConsultantSalaryViewTab: React.FC<{ consultant: Consultant }> = ({ consultant }) => {
	const { data: organisation } = useOrganisation();
	const navigate = useNavigate();
	const { t } = useTranslation();

	// Note: Make use of this when adding range filters etc
	const [selectedDateRange, setSelectedDateRange] = useState({
		startDate: dayjs().startOf("month").toDate(),
		endDate: dayjs().endOf("month").toDate(),
	});

	const { salary, isLoading: isLoadingSalaryData } = useConsultantSalarySummary(
		consultant.id,
		dayjs(selectedDateRange.startDate).toISOString(),
		dayjs(selectedDateRange.endDate).toISOString()
	);

	const { data: applications, isLoading: isLoadingApplications } =
		useConsultantApplications<ApplicationWithJobAndShifts>({
			consultantId: consultant.id,
			expand: ["job", "job.customer", "shifts"],
			page: 1,
			pageSize: 2000,
			status: [APPLICATION_STATUS.ACCEPTED, APPLICATION_STATUS.PENDING_ANSWER],
			startDate: selectedDateRange.startDate.toISOString(),
			endDate: selectedDateRange.endDate.toISOString(),
			includeOverlappingRange: true,
		});

	const isLoading = isLoadingSalaryData || isLoadingApplications;

	const salaryStats = useMemo(() => {
		const data = {
			hourStats: {
				total: 0,
				items: [] as SalaryStatItem[],
			},
			amountStats: {
				total: 0,
				items: [] as SalaryStatItem[],
			},
		};

		if (salary) {
			// Note: Break is already deducted from workedHours
			// Total booked time = worked + sick + VAB (workedHours excludes absence hours)
			data.hourStats.total =
				(salary.workedHours ?? 0) +
				getNumberValue(salary.sickHours) +
				getNumberValue(salary.vabHours);

			data.hourStats.items = [
				{ label: "WORKED_HOURS", figure: salary.workedHours ?? 0 },
				// TODO: Show VAB hours if we can get it from the API
				{ label: "SICK_LEAVE", figure: salary.sickHours ?? 0 },
				{ label: "VAB", figure: salary.vabHours ?? 0 },
			];

			data.amountStats.items = [
				{ label: "salary.hourlyPay", figure: salary.totalSalary ?? 0 },
				...(salary.obTotal && salary.obTotal > 0
					? [{ label: "salary.ob", figure: salary.obTotal }]
					: []),
				{ label: "HOLIDAY_PAY", figure: salary.holidayPay ?? 0 },
				{ label: "salary.sickPay", figure: salary.sickSalary ?? 0 },
				{ label: "salary.qualifyingDeduction", figure: (salary.qualifyingDeduction ?? 0) * -1 },
				{ label: "salary.grossSalary", figure: salary.grossSalary ?? 0, isEmphasized: true },
				{ label: "DEDUCTION_AMOUNT", figure: (salary.deduction ?? 0) * -1 },
			];
		}
		return data;
	}, [salary]);

	const rows = useMemo(() => {
		if (!applications?.length) return [];

		const groupedApplicationByJob = applications.reduce<
			Record<string, ApplicationWithJobAndShifts[]>
		>((acc, curr) => {
			// Consultant may have multiple applications for the same job
			if (acc[curr.job.id]) {
				acc[curr.job.id].push(curr);
			} else {
				acc[curr.job.id] = [curr];
			}

			return acc;
		}, {});

		const jobRows = Object.keys(groupedApplicationByJob).map<ScheduleRow>((jobId) => {
			const applications = groupedApplicationByJob[jobId];

			const job = applications[0].job;

			const shiftsWithApplicationStatus = applications.reduce<
				(Shift & { applicationStatus: APPLICATION_STATUS })[]
			>((acc, curr) => {
				for (const shift of curr.shifts) {
					const existingEntry = acc.find((s) => s.id === shift.id);

					if (existingEntry && existingEntry.applicationStatus !== APPLICATION_STATUS.ACCEPTED) {
						// Consultant may have multiple applications for the same shift but only one can be accepted
						// so favor accepted over pending
						existingEntry.applicationStatus = curr.status;
					} else if (!existingEntry) {
						acc.push({ ...shift, applicationStatus: curr.status });
					}
				}

				return acc;
			}, []);

			return {
				id: jobId,
				title: (
					<>
						<AppText
							textAlign="right"
							weight="medium"
							cursor="pointer"
							size="s"
							onClick={() => navigate(customerRoute(job.customer.id))}
						>
							{job.customer.name}
						</AppText>
						<AppText
							textAlign="right"
							cursor="pointer"
							color="grayText"
							onClick={() => navigate(jobRoute(job.id))}
						>
							<>
								#{job.orderNumber},{" "}
								{getServiceName(organisation, job.competence.branch, job.competence.service)}
							</>
						</AppText>
					</>
				),
				onClick: () => {},
				events: shiftsWithApplicationStatus.map(({ applicationStatus, ...shift }) => ({
					...shift,
					id: shift.id!,
					type: applicationStatus === APPLICATION_STATUS.ACCEPTED ? "shift-booked" : "shift-open",
					job,
					shift,
				})),
			};
		});

		return jobRows;
	}, [applications, consultant, navigate, organisation]);

	const handlePrevMonth = () => {
		setSelectedDateRange((prevState) => {
			const newStartDate = dayjs(prevState.startDate)
				.subtract(1, "month")
				.startOf("month")
				.toDate();
			const newEndDate = dayjs(prevState.endDate).subtract(1, "month").endOf("month").toDate();
			return { startDate: newStartDate, endDate: newEndDate };
		});
	};

	const handleNextMonth = () => {
		setSelectedDateRange((prevState) => {
			const newStartDate = dayjs(prevState.startDate).add(1, "month").startOf("month").toDate();
			const newEndDate = dayjs(prevState.endDate).add(1, "month").endOf("month").toDate();
			return { startDate: newStartDate, endDate: newEndDate };
		});
	};

	// Format selected month as "YYYY-MM" for API
	const selectedMonth = dayjs(selectedDateRange.startDate).format("YYYY-MM");

	return (
		<div>
			<Flex justifyContent="space-between">
				{/* <div className="w-[250px]" /> */}
				<MonthSelector
					value={selectedDateRange}
					handleNextMonth={handleNextMonth}
					handlePrevMonth={handlePrevMonth}
				/>
				<AppText
					margin="1rem 0"
					weight="bold"
					color="grayText"
					textAlign="right"
					className="w-[250px]"
				>
					{isLoading
						? "Laddar..."
						: `${t("AVERAGE_WEEK_TIME")} : ${formatNumber(salary.workedHoursWeeklyAvg ?? 0)} h`}
				</AppText>
			</Flex>
			{isLoading ? (
				<LoadingIndicator className="mt-6" />
			) : (
				<VStack>
					<div className="max-w-[88vw] h-[300px]">
						<Schedule
							shouldShowMonthLabel={false}
							shiftTooltipType={TooltipType.ShiftSalaryDetails}
							rows={rows}
							range={selectedDateRange}
							footer={
								<div className="flex flex-row gap-4 mt-3">
									<div className="min-w-[400px] flex flex-col gap-6">
										<SalaryBoxData
											title={t("HOURS")}
											unitType={"hour"}
											itemList={salaryStats.hourStats.items}
											total={salaryStats.hourStats.total}
											totalLabel={"salary.totalHours"}
										/>
										<GuaranteedHoursProgress consultantId={consultant.id} month={selectedMonth} />
									</div>
									<SalaryBoxData
										title={t("SALARY_VIEW")}
										unitType={"amount"}
										itemList={salaryStats.amountStats.items}
									/>
								</div>
							}
						/>
					</div>
				</VStack>
			)}
		</div>
	);
};

export default ConsultantSalaryViewTab;
interface SalaryBoxDataProps {
	itemList: SalaryStatItem[];
	unitType: "hour" | "amount";
	title: string;
	total?: number;
	totalLabel?: string;
}

const SalaryBoxData = ({ itemList, unitType, title, total, totalLabel }: SalaryBoxDataProps) => {
	const { t } = useTranslation();
	return (
		<VStack w={"100%"} alignItems={"flex-start"}>
			<FormLabel>{title}</FormLabel>
			<VStack
				w={"100%"}
				bg={"#FFFFFF"}
				borderRadius={"5px"}
				border={"1px solid #DBDBDB"}
				p={5}
				justifyContent={"flex-start"}
			>
				{itemList.map((item, i) => (
					<Fragment key={i}>
						{item.isEmphasized && <Divider />}
						<HStack w={"100%"} justifyContent={"space-between"}>
							<Text fontWeight={item.isEmphasized ? 700 : 400} fontSize={"15px"}>
								{t(item.label as any)}
							</Text>
							<Text fontWeight={item.isEmphasized ? 700 : 500} fontSize={"15px"}>
								{unitType === "hour"
									? `${formatNumber(item.figure)} h`
									: formatCurrencySEK(item.figure)}
							</Text>
						</HStack>
					</Fragment>
				))}
				{totalLabel && (
					<>
						<Divider />
						<HStack w={"100%"} justifyContent={"space-between"}>
							<Text fontWeight={700} fontSize={"15px"}>
								{t(totalLabel as any)}
							</Text>
							<Text fontWeight={700} fontSize={"15px"}>
								{unitType === "hour"
									? `${formatNumber(total ?? 0)} h`
									: formatCurrencySEK(total ?? 0)}
							</Text>
						</HStack>
					</>
				)}
			</VStack>
		</VStack>
	);
};
